/**
 * POI Lists Store - Pinia
 *
 * Manages logical list/group containers for POIs.
 */

import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';

const DEFAULT_POI_LIST_TITLE = 'Default POIs';

function normalizeTitle (value) {
  return String(value || '').trim();
}

function sortPoiLists (rows) {
  return [...rows].sort((a, b) => {
    const aDefault = Boolean(a?.isDefault);
    const bDefault = Boolean(b?.isDefault);
    if (aDefault !== bDefault) return aDefault ? -1 : 1;

    const aTitle = String(a?.title || '').toLowerCase();
    const bTitle = String(b?.title || '').toLowerCase();
    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);

    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

function findById (rows, id) {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) return null;
  return rows.find(row => Number(row?.id) === numericId) || null;
}

async function readDefaultFromIndex () {
  if (!GeoMoverDB.db) return null;

  return new Promise((resolve, reject) => {
    try {
      const tx = GeoMoverDB.db.transaction(['poiLists'], 'readonly');
      const store = tx.objectStore('poiLists');
      if (!store.indexNames.contains('isDefault')) {
        resolve(null);
        return;
      }

      const req = store.index('isDefault').get(true);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error('Failed reading default POI list by index.'));
    }
    catch (error) {
      reject(error);
    }
  });
}

async function reassignPoisToList (fromListId, toListId) {
  if (!GeoMoverDB.db) return 0;

  const fromId = Number(fromListId);
  const toId = Number(toListId);
  if (!Number.isFinite(fromId) || !Number.isFinite(toId) || fromId === toId) return 0;

  return new Promise((resolve, reject) => {
    let updatedCount = 0;

    try {
      const tx = GeoMoverDB.db.transaction(['pois'], 'readwrite');
      const store = tx.objectStore('pois');
      if (!store.indexNames.contains('listId')) {
        resolve(0);
        return;
      }

      const index = store.index('listId');
      const hasKeyRange = typeof IDBKeyRange !== 'undefined' && typeof IDBKeyRange.only === 'function';
      const request = hasKeyRange
        ? index.openCursor(IDBKeyRange.only(fromId))
        : index.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) return;

        if (hasKeyRange || Number(cursor.key) === fromId) {
          const row = cursor.value;
          cursor.update({
            ...row,
            listId: toId,
            updatedAt: Date.now(),
            version: Math.max(1, Number(row?.version || 1) + 1),
            versionUpdatedAt: Date.now(),
            versionUpdatedByContext: 'list:reassign'
          });
          updatedCount++;
        }

        cursor.continue();
      };

      request.onerror = () => reject(request.error || new Error('Failed iterating POIs by listId.'));
      tx.oncomplete = () => resolve(updatedCount);
      tx.onerror = () => reject(tx.error || new Error('Failed to reassign POIs to fallback list.'));
    }
    catch (error) {
      reject(error);
    }
  });
}

export const usePoiListsStore = defineStore('poiLists', {
  state: () => ({
    poiLists: [],
    isLoaded: false
  }),

  getters: {
    getAllPoiLists: (state) => state.poiLists,
    getPoiListById: (state) => (id) => findById(state.poiLists, id),
    defaultPoiList: (state) => state.poiLists.find(row => Boolean(row?.isDefault)) || null,
    defaultPoiListId () {
      return this.defaultPoiList ? Number(this.defaultPoiList.id) : null;
    }
  },

  actions: {
    async loadPoiLists () {
      try {
        const rows = await GeoMoverDB.findAll('poiLists');
        this.poiLists = sortPoiLists(Array.isArray(rows) ? rows : []);
        this.isLoaded = true;
        return this.poiLists;
      }
      catch (error) {
        appDebugLog.error('PoiListsStore', 'Error loading POI lists:', error);
        this.poiLists = [];
        this.isLoaded = false;
        return [];
      }
    },

    async ensureDefaultPoiList () {
      if (!this.isLoaded) {
        await this.loadPoiLists();
      }

      if (this.defaultPoiList) {
        return this.defaultPoiList;
      }

      try {
        const indexedDefault = await readDefaultFromIndex();
        if (indexedDefault) {
          this.poiLists = sortPoiLists([
            ...this.poiLists.filter(row => Number(row?.id) !== Number(indexedDefault.id)),
            indexedDefault
          ]);
          this.isLoaded = true;
          return indexedDefault;
        }
      }
      catch (error) {
        appDebugLog.warn('PoiListsStore', 'Could not resolve default POI list from index:', error);
      }

      const payload = {
        title: DEFAULT_POI_LIST_TITLE,
        isDefault: true,
        createdAt: Date.now(),
        updatedAt: null
      };

      try {
        const id = await GeoMoverDB.addOne('poiLists', payload);
        const persisted = { ...payload, id };
        this.poiLists = sortPoiLists([...this.poiLists, persisted]);
        this.isLoaded = true;
        appDebugLog.info('PoiListsStore', `Seeded fallback list: ${DEFAULT_POI_LIST_TITLE}`);
        return persisted;
      }
      catch (error) {
        appDebugLog.error('PoiListsStore', 'Failed to seed fallback POI list:', error);
        throw error;
      }
    },

    async createPoiList (input) {
      const title = normalizeTitle(input?.title);
      if (!title) {
        throw new Error('POI list title is required.');
      }

      const now = Date.now();
      const payload = {
        title,
        isDefault: Boolean(input?.isDefault),
        createdAt: now,
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne('poiLists', payload);
      const persisted = { ...payload, id };
      this.poiLists = sortPoiLists([...this.poiLists, persisted]);
      this.isLoaded = true;

      if (payload.isDefault) {
        await this.setDefaultPoiList(id);
      }

      return persisted;
    },

    async updatePoiList (listId, updates = {}) {
      const numericId = Number(listId);
      if (!Number.isFinite(numericId)) throw new Error('Valid POI list ID is required.');

      const current = this.getPoiListById(numericId) || await GeoMoverDB.findOne('poiLists', numericId);
      if (!current) throw new Error('POI list not found.');

      const nextTitle = updates.title !== undefined ? normalizeTitle(updates.title) : current.title;
      if (!nextTitle) throw new Error('POI list title is required.');

      const next = {
        ...current,
        title: nextTitle,
        updatedAt: Date.now()
      };

      await GeoMoverDB.updateOne('poiLists', next, numericId);

      this.poiLists = sortPoiLists([
        ...this.poiLists.filter(row => Number(row?.id) !== numericId),
        next
      ]);
      this.isLoaded = true;

      if (updates.isDefault === true) {
        await this.setDefaultPoiList(numericId);
      }

      return next;
    },

    async setDefaultPoiList (listId) {
      const numericId = Number(listId);
      if (!Number.isFinite(numericId)) throw new Error('Valid POI list ID is required.');

      if (!this.isLoaded) {
        await this.loadPoiLists();
      }

      const target = this.getPoiListById(numericId) || await GeoMoverDB.findOne('poiLists', numericId);
      if (!target) throw new Error('POI list not found.');

      const previousDefaultId = this.defaultPoiListId;
      if (previousDefaultId === numericId) return target;

      if (previousDefaultId != null) {
        const previous = this.getPoiListById(previousDefaultId);
        if (previous) {
          const prevUpdate = {
            ...previous,
            isDefault: false,
            updatedAt: Date.now()
          };
          await GeoMoverDB.updateOne('poiLists', prevUpdate, Number(previous.id));
        }
      }

      const next = {
        ...target,
        isDefault: true,
        updatedAt: Date.now()
      };

      await GeoMoverDB.updateOne('poiLists', next, numericId);
      await this.loadPoiLists();
      return next;
    },

    async deletePoiList (listId, options = {}) {
      const numericId = Number(listId);
      if (!Number.isFinite(numericId)) throw new Error('Valid POI list ID is required.');

      if (!this.isLoaded) {
        await this.loadPoiLists();
      }

      const target = this.getPoiListById(numericId) || await GeoMoverDB.findOne('poiLists', numericId);
      if (!target) return false;
      if (target.isDefault) {
        throw new Error('Default POI list cannot be deleted.');
      }

      const reassignPois = options.reassignPois !== false;
      if (reassignPois) {
        const fallback = await this.ensureDefaultPoiList();
        await reassignPoisToList(numericId, Number(fallback.id));
      }

      await GeoMoverDB.deleteOne('poiLists', numericId);
      this.poiLists = sortPoiLists(this.poiLists.filter(row => Number(row?.id) !== numericId));
      return true;
    }
  }
});
